import { useCallback, useEffect, useState } from "react";
import { LensChart } from "./LensChart";
import { MapCanvas } from "./MapCanvas";
import { integer, money, percent } from "./format";
import type { AtlasData, LensStats, Metric } from "./types";

const EMPTY_LENS: LensStats = {
  records: 0,
  parcels: 0,
  value: 0,
  overFloor: 0,
  bands: [0, 0, 0, 0, 0, 0],
  boroughs: [0, 0, 0, 0, 0],
  classes: [0, 0],
  cellCount: 0
};

const metrics: { id: Metric; label: string; description: string }[] = [
  { id: "records", label: "Roll rows", description: "Every published row" },
  { id: "value", label: "DOF value", description: "Sum of final market values" },
  {
    id: "overFloor",
    label: "Above floor",
    description: "Rows above the public class value floor"
  }
];

function Loading() {
  return (
    <main className="loading-screen">
      <div className="loading-orbit" aria-hidden="true">
        <span />
      </div>
      <p>Switching on 959,710 little lights…</p>
    </main>
  );
}

export default function App() {
  const [data, setData] = useState<AtlasData | null>(null);
  const [error, setError] = useState("");
  const [metric, setMetric] = useState<Metric>("overFloor");
  const [lens, setLens] = useState<LensStats>(EMPTY_LENS);
  const [aboutOpen, setAboutOpen] = useState(false);

  useEffect(() => {
    fetch(`${import.meta.env.BASE_URL}data/atlas.json`)
      .then((response) => {
        if (!response.ok) throw new Error(`Data request returned ${response.status}`);
        return response.json();
      })
      .then(setData)
      .catch(() =>
        setError("The map data did not arrive. Refresh once; if it still hides, try again shortly.")
      );
  }, []);

  useEffect(() => {
    if (!aboutOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setAboutOpen(false);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [aboutOpen]);

  const handleLensChange = useCallback((next: LensStats) => setLens(next), []);

  if (error) {
    return (
      <main className="error-screen">
        <p className="eyebrow">THE CITY IS PLAYING COY</p>
        <h1>We lost the roll.</h1>
        <p>{error}</p>
      </main>
    );
  }
  if (!data) return <Loading />;

  const joinRate = percent(data.totals.joinedRecords, data.totals.records);
  const selectedMetric = metrics.find((item) => item.id === metric)!;

  return (
    <>
      <header className="site-header">
        <a className="wordmark" href="#top" aria-label="Who left the lights on? Home">
          <span className="wordmark-mark">●</span>
          SECOND ADDRESS
        </a>
        <button className="about-button" onClick={() => setAboutOpen(true)}>
          Read this first
        </button>
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">NYC · FY 2027 · AN EXPLORATORY MAP</p>
            <h1>
              Who left the
              <br />
              <em>lights on?</em>
            </h1>
            <p className="hero-deck">
              The city published a roll of homes that <strong>includes—but is not limited to</strong>
              —properties that may face its new non-primary-residence surcharge. Put a spotlight on
              the pattern.
            </p>
          </div>
          <div className="hero-number">
            <span className="number-kicker">THE ROLL CONTAINS</span>
            <strong>{integer.format(data.totals.records)}</strong>
            <span>rows. Not verdicts.</span>
          </div>
        </section>

        <section className="map-section" aria-labelledby="map-title">
          <div className="map-heading">
            <div>
              <p className="section-number">01 / FIND THE GLOW</p>
              <h2 id="map-title">Move the spotlight.</h2>
            </div>
            <div className="metric-tabs" role="group" aria-label="Choose a map metric">
              {metrics.map((item) => (
                <button
                  key={item.id}
                  className={metric === item.id ? "active" : ""}
                  onClick={() => setMetric(item.id)}
                  aria-pressed={metric === item.id}
                  title={item.description}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </div>

          <div className="map-grid">
            <MapCanvas cells={data.cells} metric={metric} onLensChange={handleLensChange} />
            <aside className="lens-panel" aria-live="polite">
              <div className="lens-panel-top">
                <p className="eyebrow">INSIDE THE SPOTLIGHT</p>
                <span className="metric-chip">{selectedMetric.label}</span>
              </div>
              <div className="lens-total">
                <strong>{integer.format(lens.records)}</strong>
                <span>published rows</span>
              </div>
              <div className="mini-stats">
                <div>
                  <span>DOF value</span>
                  <strong>{money(lens.value)}</strong>
                </div>
                <div>
                  <span>Above floor*</span>
                  <strong>{integer.format(lens.overFloor)}</strong>
                </div>
              </div>
              <div className="chart-header">
                <h3>How the values stack up</h3>
                <span>{lens.cellCount} map cells</span>
              </div>
              <LensChart stats={lens} />
              <p className="fine-print">
                *A row above the headline class floor is not a tax determination. Residence,
                exemptions, appeals, and record type still matter.
              </p>
            </aside>
          </div>
        </section>

        <section className="reality-check">
          <div className="section-intro">
            <p className="section-number">02 / THE BOROUGH READ</p>
            <h2>The list is broad by design.</h2>
            <p>
              It covers ordinary homes, condos, co-op buildings, and individual co-op units. The
              biggest pile of rows is not automatically the biggest pile of second homes.
            </p>
          </div>
          <div className="borough-grid">
            {data.boroughStats.map((borough, index) => (
              <article className="borough-card" key={borough.name}>
                <span className="borough-index">0{index + 1}</span>
                <h3>{borough.name}</h3>
                <strong>{integer.format(borough.records)}</strong>
                <span>roll rows</span>
                <div className="borough-rule">
                  <i
                    style={{
                      width: `${(borough.records / data.boroughStats[3].records) * 100}%`
                    }}
                  />
                </div>
                <small>{integer.format(borough.overFloor)} above the class value floor*</small>
              </article>
            ))}
          </div>
        </section>

        <section className="caveat">
          <p className="section-number">03 / THE IMPORTANT PART</p>
          <div className="caveat-grid">
            <h2>
              A dot is
              <br />
              <em>not a bill.</em>
            </h2>
            <div>
              <p>
                The Department of Finance says not every property or unit on this roll will be
                subject to the surcharge. Only owners who received a mailed notice are required to
                act.
              </p>
              <p>
                This project intentionally omits owner names and exact addresses. It maps aggregated
                tax-lot coordinates for public-interest exploration, not personal lookup.
              </p>
              <button className="text-button" onClick={() => setAboutOpen(true)}>
                See the method and limitations →
              </button>
            </div>
          </div>
        </section>

        <section className="sources" id="sources">
          <p className="section-number">04 / RECEIPTS</p>
          <h2>Built from the city’s own files.</h2>
          <div className="source-grid">
            <a href="https://www.nyc.gov/site/finance/property/property-assessments.page">
              <span>NYC Department of Finance</span>
              Supplemental market value roll ↗
            </a>
            <a href="https://www.nyc.gov/site/finance/property/non-primary-residence-surcharge.page">
              <span>NYC Department of Finance</span>
              Surcharge rules and FAQs ↗
            </a>
            <a href="https://data.cityofnewyork.us/City-Government/Primary-Land-Use-Tax-Lot-Output-PLUTO-/64uk-42ks">
              <span>NYC Department of City Planning</span>
              PLUTO tax-lot coordinates ↗
            </a>
          </div>
          <p className="join-note">
            {joinRate} of roll rows matched a current PLUTO tax lot or condo complex. Unmatched
            rows remain in citywide and borough totals but are not invented onto the map.
          </p>
        </section>
      </main>

      <footer>
        <span>SECOND ADDRESS · A PUBLIC-DATA EXPLORATION</span>
        <span>ROLL PUBLISHED JULY 24, 2026</span>
      </footer>

      {aboutOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={() => setAboutOpen(false)}>
          <section
            className="about-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="about-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <button
              className="dialog-close"
              onClick={() => setAboutOpen(false)}
              aria-label="Close methodology"
            >
              ×
            </button>
            <p className="eyebrow">BEFORE YOU DRAW A CONCLUSION</p>
            <h2 id="about-title">What this map can—and cannot—say.</h2>
            <p>
              The July 2026 supplemental roll is a public-inspection dataset connected to the new
              surcharge. The city explicitly says it includes more than the properties that may
              ultimately owe.
            </p>
            <ol>
              <li>
                <strong>Rows are not taxpayers.</strong> Co-op buildings can appear alongside
                individual units, and one location may generate many rows.
              </li>
              <li>
                <strong>“Above floor” is only a screen.</strong> It compares listed FMV to the
                public $5m Class 1 or $1m Class 2 headline floor; it does not test residence or
                exemptions.
              </li>
              <li>
                <strong>The map is aggregated.</strong> Rows join to current PLUTO coordinates and
                are binned into roughly 300–400m cells. No owner names or exact addresses ship to
                the browser.
              </li>
            </ol>
            <a
              className="dialog-source"
              href="https://www.nyc.gov/site/finance/property/non-primary-residence-surcharge.page"
            >
              Read the city’s official surcharge page ↗
            </a>
          </section>
        </div>
      )}
    </>
  );
}
